import type { KnownRoute } from './routes';

/**
 * A route pattern that can optionally end with `/*` for prefix matching.
 *
 * - `'/workspaces/:workspaceId/journeys/campaigns'` — exact match only
 * - `'/workspaces/:workspaceId/journeys/campaigns/*'` — matches any sub-path
 */
export type RoutePattern = KnownRoute | `${KnownRoute}/*`;

/**
 * Check if a pathname matches any of the given route patterns.
 *
 * @example
 * matchRoutes('/workspaces/123/journeys/campaigns/42/overview', [
 *   '/workspaces/:workspaceId/journeys/campaigns/:campaignId/*',
 * ])
 * // => true
 */
export function matchRoutes(pathname: string, patterns: RoutePattern[]): boolean {
  return patterns.some((pattern) => matchRoutePattern(pathname, pattern));
}

export function matchRoutePattern(pathname: string, pattern: string): boolean {
  return matchRouteParams(pathname, pattern) != null;
}

export function matchRouteParams(
  pathname: string,
  pattern: string,
  options: { end?: boolean } = {},
): Record<string, string> | null {
  const end = options.end ?? true;
  const isPrefixMatch = pattern.endsWith('/*');
  const patternWithoutSplat = isPrefixMatch ? pattern.slice(0, -2) : pattern;
  const pathnameSegments = getPathSegments(pathname);
  const patternSegments = getPathSegments(patternWithoutSplat);
  const allowPrefixMatch = isPrefixMatch || !end;

  if (allowPrefixMatch) {
    if (pathnameSegments.length < patternSegments.length) {
      return null;
    }
  } else if (pathnameSegments.length !== patternSegments.length) {
    return null;
  }

  return collectParams(pathnameSegments, patternSegments);
}

function getPathSegments(path: string) {
  return path.split('/').filter((segment) => segment.length > 0);
}

function segmentMatches(pathSegment: string | undefined, patternSegment: string) {
  if (pathSegment == null) {
    return false;
  }

  return patternSegment.startsWith(':') || pathSegment === patternSegment;
}

function collectParams(pathnameSegments: string[], patternSegments: string[]) {
  const params: Record<string, string> = {};

  for (const [index, patternSegment] of patternSegments.entries()) {
    const pathnameSegment = pathnameSegments[index];
    if (!segmentMatches(pathnameSegment, patternSegment)) {
      return null;
    }

    if (patternSegment.startsWith(':') && pathnameSegment != null) {
      params[patternSegment.slice(1)] = pathnameSegment;
    }
  }

  return params;
}
